import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "../../axios";
import "dropify/dist/css/dropify.min.css";
import "dropify/dist/js/dropify.min.js";
import $ from "jquery";

const AddBlog = () => {
  const { id } = useParams();
  const isEditing = !!id;
  const navigate = useNavigate(); // Add useNavigate hook

  const [title, setTitle] = useState("");
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [description, setDescription] = useState("");
  const [subheading, setSubheading] = useState("");
  const [content, setContent] = useState("");
  const [sections, setSections] = useState([]);
  const fileInputRef = useRef(null);

  const sanitizeText = (text) => {
    return text
      ?.replace(/\u2028/g, "\n")
      .replace(/\u2029/g, "\n")
      .replace(/\r\n|\r/g, "\n");
  };

  useEffect(() => {
    if (isEditing) {
      axios
        .get(`/blogs/${id}`)
        .then((res) => {
          const blog = res.data;
          setTitle(blog.title);
          setDescription(sanitizeText(blog.description));
          setSubheading(blog.subheading);
          setContent(sanitizeText(blog.content));

          const sanitizedSections = (blog.sections ?? []).map((s) => ({
            subheading: s.subheading,
            content: sanitizeText(s.content),
          }));
          setSections(sanitizedSections);

          setImagePreview(blog.image_url);
        })
        .catch((err) => {
          console.error("Error fetching blog:", err);
          alert("Could not load blog data");
        });
    }
  }, [id, isEditing]);

  useEffect(() => {
    if (isEditing && imagePreview) {
      const $input = $(fileInputRef.current);
      const drEvent = $input.data("dropify");
      if (drEvent) drEvent.destroy();

      $input.attr("data-default-file", imagePreview);

      setTimeout(() => {
        $input.dropify();
      }, 100);
    }
  }, [isEditing, imagePreview]);

  useEffect(() => {
    if (!isEditing) {
      const $input = $(fileInputRef.current);
      const drEvent = $input.data("dropify");
      if (drEvent) drEvent.destroy();

      $input.removeAttr("data-default-file");

      setTimeout(() => {
        $input.dropify();
      }, 100);
    }
  }, [isEditing]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setImage(file);
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleAddSection = () => {
    setSections([...sections, { subheading: "", content: "" }]);
  };

  const handleDeleteSection = (index) => {
    const updated = [...sections];
    updated.splice(index, 1);
    setSections(updated);
  };

  const handleSectionChange = (index, field, value) => {
    const updated = [...sections];
    updated[index][field] = value;
    setSections(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const sanitizedSections = sections.map((s) => ({
      subheading: s.subheading,
      content: sanitizeText(s.content),
    }));

    const formData = new FormData();
    formData.append("title", title);
    formData.append("description", sanitizeText(description));
    formData.append("subheading", subheading);
    formData.append("content", sanitizeText(content));
    formData.append("sections", JSON.stringify(sanitizedSections));
    if (image) formData.append("image", image);

    try {
      if (isEditing) await axios.post(`/blogs/${id}?_method=PUT`, formData);
      else await axios.post("/blogs", formData);

      alert(`Blog ${isEditing ? "updated" : "created"} successfully!`);
      // window.location.href = "/admin/blogList";
      navigate("/admin/blogList");
    } catch (error) {
      console.error("Error saving blog:", error.response?.data);
      alert("Failed to save blog");
    }
  };

  return (
    <div className="main-content">
      <Link to="/admin/blogList" className="text-decoration-none">
        <button className="btn text-black mb-3 border-0">
          <h5>
            ←{" "}
            <span className="ms-2">{isEditing ? "Edit Blog" : "Add Blog"}</span>
          </h5>
        </button>
      </Link>

      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          <label className="form-label">
            Blog Title <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className="form-control"
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>

        <div className="mb-3">
          <label className="form-label">
            Cover Image <span className="text-danger">*</span>
          </label>
          <input
            type="file"
            className="form-control dropify"
            ref={fileInputRef}
            onChange={handleImageChange}
            accept="image/*"
            required={!isEditing}
          />
        </div>

        <div className="mb-3">
          <label className="form-label">
            Description <span className="text-danger">*</span>
          </label>
          <textarea
            className="form-control"
            rows="3"
            required
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div className="mb-3">
          <label className="form-label">
            Subheading <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className="form-control"
            required
            value={subheading}
            onChange={(e) => setSubheading(e.target.value)}
          />
        </div>

        <div className="mb-3">
          <label className="form-label">
            Content <span className="text-danger">*</span>
          </label>
          <textarea
            className="form-control"
            rows="6"
            required
            value={content}
            onChange={(e) => setContent(e.target.value)}
          />
        </div>

        <div className="text-end py-3">
          <button
            type="button"
            className="btn btn-dark py-2 px-4 w-auto rounded-0"
            onClick={handleAddSection}>
            + Add Section
          </button>
        </div>

        {sections.map((section, index) => (
          <div key={index}>
            <div className="mb-3">
              <label className="form-label">
                Section Subheading <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                required
                value={section.subheading}
                onChange={(e) =>
                  handleSectionChange(index, "subheading", e.target.value)
                }
              />
            </div>
            <div className="mb-3">
              <label className="form-label">
                Section Content <span className="text-danger">*</span>
              </label>
              <textarea
                className="form-control"
                rows="4"
                required
                value={section.content}
                onChange={(e) =>
                  handleSectionChange(index, "content", e.target.value)
                }
              />
            </div>
            <div className="text-end">
              <button
                type="button"
                className="btn del-sec py-2 px-4 w-auto rounded-0"
                onClick={() => handleDeleteSection(index)}>
                - Delete Section
              </button>
            </div>
          </div>
        ))}

        <div className="mt-4">
          <button
            type="submit"
            className="btn gold-add-btn py-2 px-4 w-auto rounded-0 text-white">
            {isEditing ? "Update" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddBlog;
